import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import type { AuthPrincipal, User } from '@mg/shared';
import { api, ApiRequestError, configureApi } from '../lib/api';
import {
  clearStoredGoogleToken,
  forgetGoogleAccount,
  googleSignInAvailable,
  googleTokenExpired,
  readStoredGoogleToken,
  storeGoogleToken,
} from '../lib/googleSignIn';
import { devAuthEnabled } from '../lib/env';
import { getMsalInstance, loginRequest, pickAccount } from '../lib/msal';
import { getActiveTenantSlug } from './activeTenant';

type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated' | 'error';

interface AuthContextValue {
  status: AuthStatus;
  principal: AuthPrincipal | null;
  error: string | null;
  /** Trigger interactive sign-in (Entra). No-op in dev-auth mode. */
  signIn: () => Promise<void>;
  /**
   * Finish a Google sign-in: called with the ID token Google delivered to its own button.
   * The app no longer initiates this - `GoogleSignInButton` lets Google own the press, which is
   * what fixed a button that pressed to no effect. See `lib/googleSignIn.ts`.
   */
  completeGoogleSignIn: (credential: string) => Promise<void>;
  /** True when this install offers Google sign-in and the API judged it usable. */
  googleAvailable: boolean;
  signOut: () => Promise<void>;
  /** Re-fetch the principal (e.g. after switching tenant). */
  refresh: () => Promise<void>;
  /**
   * Fold a saved user back into the principal, without a reload. `refresh` drops the whole app
   * back to "Signing you in…" and remounts the page, which is too much for flipping one setting.
   */
  updateUser: (user: User) => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Provides the authenticated principal to the app. Two modes:
 *  - dev-auth: skip MSAL, just GET /api/auth/me (API injects the seeded dev user).
 *  - Entra: acquire a token silently (or interactively), then GET /api/auth/me.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [principal, setPrincipal] = useState<AuthPrincipal | null>(null);
  const [error, setError] = useState<string | null>(null);
  const tokenRef = useRef<string | null>(null);

  const acquireToken = useCallback(async (): Promise<string | null> => {
    if (devAuthEnabled) return null;
    const msal = getMsalInstance();
    const account = pickAccount(msal);
    if (!account) return null;
    try {
      // Silent acquisition returns the cached access token when still valid and
      // transparently renews it from the refresh token when it has expired.
      const result = await msal.acquireTokenSilent({ ...loginRequest, account });
      return result.accessToken;
    } catch {
      return null;
    }
  }, []);

  // Force a brand-new access token (bypass MSAL's cache) — used to recover from a 401
  // caused by an expired token. If the SESSION itself has expired (no usable refresh
  // token), silent renewal is impossible, so bounce to interactive sign-in.
  const refreshToken = useCallback(async (): Promise<string | null> => {
    if (devAuthEnabled) return null;
    const msal = getMsalInstance();
    const account = pickAccount(msal);
    if (!account) return null;
    try {
      const result = await msal.acquireTokenSilent({
        ...loginRequest,
        account,
        forceRefresh: true,
      });
      tokenRef.current = result.accessToken;
      return result.accessToken;
    } catch (err) {
      if (err instanceof InteractionRequiredAuthError) {
        // Only one interactive redirect can run at a time; concurrent 401s make the
        // losers throw interaction_in_progress. Swallow it so this always resolves to
        // null (the winning redirect navigates the page away regardless).
        try {
          await msal.acquireTokenRedirect({ ...loginRequest, account });
        } catch {
          /* another request already started the sign-in redirect */
        }
      }
      return null;
    }
  }, []);

  // Wire the API client's providers. The token provider RE-ACQUIRES on every request
  // (acquireTokenSilent auto-renews from the refresh token), so an idle-expired token
  // never reaches the server — the root fix for the after-idle "session expired" errors.
  // `refresh` force-refreshes and retries once on a 401 (see api.ts request()).
  useEffect(() => {
    configureApi({
      token: async () => {
        // A Google install's CUSTOMER signs in with Google; its Alloterra administrators still
        // sign in with Microsoft. Both are live at once, so prefer whichever session this person
        // actually has - the API accepts a token from either issuer.
        const google = readStoredGoogleToken();
        if (google && !googleTokenExpired(google, Date.now())) {
          tokenRef.current = google;
          return google;
        }
        // Expired: drop it rather than sending a token the API will refuse, which would read as a
        // broken session instead of an ordinary expiry.
        if (google) clearStoredGoogleToken();
        tokenRef.current = await acquireToken();
        return tokenRef.current;
      },
      tenant: () => getActiveTenantSlug(),
      refresh: refreshToken,
    });
  }, [acquireToken, refreshToken]);

  /**
   * Load the principal, and REPORT the outcome rather than only setting state.
   *
   * The outcome matters because a 401 means two different things. On boot it means "not signed in",
   * and showing an error for that would put a red message on every first visit. Straight after an
   * explicit sign-in it means "your credential was refused", and the server's own words name the
   * reason — the domain is not on the customer's allowed list, nobody invited this address, the
   * token was minted for another application. Swallowing it sent the person back to the sign-in
   * card with nothing to read, which is the same silent failure as a button that does nothing.
   *
   * So the refusal is returned and the CALLER decides whether it is worth showing.
   */
  const loadPrincipal = useCallback(async (): Promise<{ ok: boolean; refusal?: string }> => {
    setStatus('loading');
    setError(null);
    try {
      const google = readStoredGoogleToken();
      if (google && !googleTokenExpired(google, Date.now())) {
        tokenRef.current = google;
      } else {
        if (google) clearStoredGoogleToken();
        tokenRef.current = await acquireToken();
      }
      if (!devAuthEnabled && !tokenRef.current) {
        setStatus('unauthenticated');
        setPrincipal(null);
        return { ok: false };
      }
      const me = await api.me();
      setPrincipal(me);
      setStatus('authenticated');
      return { ok: true };
    } catch (err) {
      if (err instanceof ApiRequestError && err.status === 401) {
        setStatus('unauthenticated');
        setPrincipal(null);
        return { ok: false, refusal: err.message };
      }
      setError(err instanceof Error ? err.message : 'Failed to load session');
      setStatus('error');
      return { ok: false };
    }
  }, [acquireToken]);

  /** The context's `refresh`: reload, discarding the outcome. */
  const refresh = useCallback(async () => {
    await loadPrincipal();
  }, [loadPrincipal]);

  useEffect(() => {
    let cancelled = false;
    const init = async () => {
      if (!devAuthEnabled) {
        const msal = getMsalInstance();
        await msal.initialize();
        // Keep the account the redirect just signed in. Discarding this result was the other half
        // of the loop described in `pickAccount`: a new account is APPENDED to the cache, so without
        // making it active the next read still returned the retired one.
        const redirect = await msal.handleRedirectPromise();
        if (redirect?.account) msal.setActiveAccount(redirect.account);
      }
      if (!cancelled) await loadPrincipal();
    };
    void init();
    return () => {
      cancelled = true;
    };
  }, [loadPrincipal]);

  const signIn = useCallback(async () => {
    if (devAuthEnabled) {
      await loadPrincipal();
      return;
    }
    const msal = getMsalInstance();
    // prompt=select_account shows the Microsoft account chooser even when the
    // browser already has an active M365 session.
    await msal.loginRedirect({ ...loginRequest, prompt: 'select_account' });
  }, [loadPrincipal]);

  /**
   * Finish a Google sign-in, for a customer whose directory is Google Workspace.
   *
   * Google Identity Services hands the browser a signed ID token; the API validates it (issuer,
   * signature, `aud` against this install's client id, and the `hd` claim against the customer's
   * allowed domains). Nothing is exchanged server-side, so there is no secret in the page.
   *
   * This runs AFTER Google's own button has produced a credential — the app does not open the
   * chooser itself. Driving it from our side meant calling `prompt()` (One Tap), which pressed to
   * no effect whenever One Tap declined to show, and whose failure could no longer be detected
   * under FedCM. `lib/googleSignIn.ts` has the detail.
   */
  const completeGoogleSignIn = useCallback(
    async (credential: string) => {
      setError(null);
      try {
        storeGoogleToken(credential);
        tokenRef.current = credential;
        const outcome = await loadPrincipal();
        if (!outcome.ok) {
          // Google signed them in; THIS SITE refused the account. Say which, in the server's own
          // words - most often the domain is not on the customer's allowed list, or nobody has
          // invited the address yet.
          clearStoredGoogleToken();
          setError(
            outcome.refusal ??
              'Google signed you in, but this site did not accept that account. Ask an administrator.',
          );
        }
      } catch (err) {
        // A token Google issued but this install refuses - most likely a domain outside the
        // customer's allowed list - has to say so, or it reads as a broken site.
        setError(err instanceof Error ? err.message : 'Google sign-in failed.');
        setStatus('unauthenticated');
      }
    },
    [loadPrincipal],
  );

  const signOut = useCallback(async () => {
    tokenRef.current = null;
    // Clear the Google session whichever way this person signed in: leaving a stored ID token
    // behind would silently sign them back in on the next page load.
    clearStoredGoogleToken();
    forgetGoogleAccount();
    if (devAuthEnabled) {
      setPrincipal(null);
      setStatus('unauthenticated');
      return;
    }
    const msal = getMsalInstance();
    await msal.logoutRedirect();
  }, []);

  const updateUser = useCallback((user: User) => {
    // Merged, not replaced: the principal's user carries fields the settings response does not
    // (isPlatformBillingAdmin), and those must survive.
    setPrincipal((prev) => (prev ? { ...prev, user: { ...prev.user, ...user } } : prev));
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      principal,
      error,
      signIn,
      completeGoogleSignIn,
      googleAvailable: googleSignInAvailable(),
      signOut,
      refresh,
      updateUser,
    }),
    [status, principal, error, signIn, completeGoogleSignIn, signOut, refresh, updateUser],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>');
  return ctx;
}
